/* ==========================================================================
   HOUND - BACK IN STOCK -> KLAVIYO
   ==========================================================================
   The notify-me form is Shopify's generic {% form 'contact' %}. On its own it
   emails the store's contact address and stops there: no customer, no marketing
   consent, no list, no per-product record. Every request for a sold-out size was
   landing in an inbox as loose text, so there was no way to send "it is back"
   to the people who asked.

   This subscribes the address to the Back in Stock list and records WHICH
   product and size they asked for, so a restock send can be segmented down to
   the variant instead of blasting everyone who ever asked for anything.

   The native form still posts afterwards, so the existing email notification is
   untouched: this is additive, and if Klaviyo is unreachable the shopper's
   request still goes through exactly as before.

   25 Oct 2026 (Katie): brought in line with the newer notify form
   (assets/hound-notify.js). Every request now also creates Klaviyo's own
   back-in-stock subscription for the exact variant picked, which is what the
   live Back in Stock flow (WtCNaw, BIS-01/02) waits on. Before this, requests
   were logged but no restock email could ever send. Joins the main HOUND list
   (VvCrKP), disclosed under the button, as the newer form does.

   Uses the same client-side endpoint and public site key the quest section
   already uses (public keys are safe in the storefront; they can only subscribe).
   ========================================================================== */
(function () {
  'use strict';

  /* The homepage renders eighteen shop sections, each of which loads this file.
     The browser fetches it once but executes it once per tag, and every copy
     would attach its own submit listener. */
  if (window.__houndBisBound) return;
  window.__houndBisBound = true;

  var PUBLIC_KEY = 'TUu56D';
  var LIST_ID = 'VvCrKP'; /* main HOUND list, as the newer notify form (was TeftQj) */
  var REVISION = '2024-10-15';

  function subscribe(email, product, variant) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + PUBLIC_KEY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock - ' + (product || 'unknown'),
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    /* Last request, for a quick segment. The event below keeps the
                       full history, since one shopper may ask about several. */
                    last_back_in_stock_product: product || null,
                    last_back_in_stock_variant: variant || null
                  }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } }
        }
      })
    }).then(function () {
      /* one ask per person: the pop-up and the Quest flag stand down */
      try {
        var st = JSON.parse(localStorage.getItem('hs-pop') || '{}'); st.joined = Date.now();
        localStorage.setItem('hs-pop', JSON.stringify(st)); localStorage.setItem('questFlagSignedUp', 'true');
      } catch (e) {}
    }).catch(function () {});
  }

  /* Klaviyo's back-in-stock watcher for the exact variant: this is what makes
     BIS-01 send when that size restocks. */
  function watch(email, variantId) {
    if (!variantId) return Promise.resolve();
    return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + PUBLIC_KEY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email: email } } } },
          relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } } }
        }
      })
    }).catch(function () {});
  }

  /* One event per request, so "everyone waiting on Heather Sweatpants in M" is a
     segment rather than a manual read of an inbox. */
  function track(email, product, variant, handle, variantId) {
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + PUBLIC_KEY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: { Product: product, Variant: variant, Handle: handle, VariantID: variantId || null, Source: 'Notify me (live PDP)' },
            metric: { data: { type: 'metric', attributes: { name: 'Requested Back in Stock' } } },
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        }
      })
    }).catch(function () {});
  }

  function context(form) {
    /* Only ever the form's OWN context. The previous fallback reached for
       document.querySelector('[data-bis-product]'), which is harmless on a
       product page with one product and actively wrong on the homepage, where
       eighteen shop cards share a document: a request for a sold-out size would
       have been filed against whichever product sat first in the DOM. Better to
       log an unknown product than a confidently wrong one. */
    var root = form.closest('[data-bis-product]');
    if (!root) {
      return { product: document.title, variant: '', handle: '', variantId: '' };
    }
    return {
      product: root.getAttribute('data-bis-product') || document.title,
      variant: root.getAttribute('data-bis-variant') || '',
      handle: root.getAttribute('data-bis-handle') || '',
      variantId: variantIdFor(root)
    };
  }

  /* The size the shopper has selected: the product form that sits beside this
     drawer holds it in its [name=id] field. buy-buttons names the two alike
     (BackInStockDrawer-<section>-<product> / ProductForm-<section>-<product>);
     failing that, the cart form in the same section. */
  function variantIdFor(root) {
    var pf = root.id ? document.getElementById(root.id.replace('BackInStockDrawer-', 'ProductForm-')) : null;
    var input = pf && pf.querySelector('[name="id"]');
    if (!input) {
      var sec = root.closest('.shopify-section, section');
      input = sec && sec.querySelector('form[action*="/cart/add"] [name="id"]');
    }
    return (input && input.value) || '';
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches('back-in-stock-drawer form, .back-in-stock form')) return;

    var field = form.querySelector('input[type="email"], input[name="contact[email]"]');
    var email = field && field.value && field.value.trim();
    if (!email || form.dataset.houndSent === '1') return;

    /* Hold the native post just long enough to register the request, then let it
       go through untouched. A failed or slow Klaviyo call must never cost the
       shopper their submission, hence the race against a short timer. */
    e.preventDefault();
    form.dataset.houndSent = '1';

    var c = context(form);
    var done = false;
    function go() {
      if (done) return;
      done = true;
      form.submit();
    }

    Promise.all([
      watch(email, c.variantId),
      subscribe(email, c.product, c.variant),
      track(email, c.product, c.variant, c.handle, c.variantId)
    ]).then(go);
    setTimeout(go, 2500);
  });
})();
